/** biome-ignore-all lint/style/useNamingConvention: camel case */
import {FocusTrap} from 'focus-trap-react';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {useSelector, useStore} from 'react-redux';
import {
  selectEventOptionAllowMaybeRsvps,
  selectEventType,
} from '~/common/fabric/reducers/eviteEvent/selectors';
import {shouldShowMaybeRSVPFilter} from '~/common/utils/event_utils';
import {fetchGuestlist} from '~/ViewInvitation/reducers/apiActions';
import {Guestlist} from '~/ViewInvitation/routes/Guests/Guestlist';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {ArrowIcon} from '~library/svg/ArrowIcon';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {largishAndUp} from '~libSass/base/_exports.sass';
import {WhoIsComingHeader} from './WhoIsComingHeader';

export const WhoIsComingOverlay = (props) => {
  const {rsvpDetails, open, onClose} = props;
  const store = useStore();
  const [query, setQuery] = useState('');
  const [openCommentEditor, setOpenCommentEditor] = useState();

  const [loading, setLoading] = useState(true);

  const eventType = useSelector(selectEventType);
  const showGifting = useSelector((state) => state.eviteEvent?.options?.event_option_show_gifting);
  const origin = useSelector((state) => state.eventDetails?.origin);
  const yesRsvp = useSelector((state) => state.guestList?.rsvpCounts?.yes);
  const maybeRsvp = useSelector((state) => state.guestList?.rsvpCounts?.maybe);
  const noRsvp = useSelector((state) => state.guestList?.rsvpCounts?.no);
  const undecided = useSelector((state) => state.guestList?.rsvpCounts?.no_reply);
  const allowMaybeRSVPs = useSelector(selectEventOptionAllowMaybeRsvps);
  const {matches: largeScreens} = useMatchQuery(`(min-width: ${largishAndUp})`);

  const isFuneral = eventType === 'funeral_memorial_service';
  const includeMaybe = shouldShowMaybeRSVPFilter({
    origin,
    allowMaybeRSVPs,
    maybeRSVPCount: maybeRsvp,
  });
  const replies = !includeMaybe ? ['yes', 'no', 'no_reply'] : ['yes', 'maybe', 'no', 'no_reply'];

  useEffect(() => {
    store.dispatch(
      fetchGuestlist(
        window.event_id,
        () => {
          setLoading(false);
        },
        (dispatch, getState, d) => {
          toastAlert(d?.results?.message || 'Error loading guestlist', {
            type: 'error',
          });
          setLoading(false);
        }
      )
    );
  }, [store]);

  useEffect(() => {
    if (open) {
      document.body.classList.add('no-scroll');
    } else {
      setOpenCommentEditor(undefined);
      document.body.classList.remove('no-scroll');
    }
  }, [open]);

  return (
    <>
      <div
        className={`view-invitation__overlay-bg ${open ? 'open' : ''}`}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClose();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="overlay background - click to close"
      />
      <FocusTrap active={open}>
        {/** TODO: Use ResponsivePanel when we update View Invitation */}
        <div className={`view-invitation__who-is-coming-overlay ${open ? 'open' : ''}`}>
          <div className="wic-overlay__heading">
            {largeScreens
              ? <>
                  <Typography variant="header5" data-qa-id="guestlist-drawer-header">
                    Guestlist
                  </Typography>
                  <button
                    data-qa-id="who-is-coming-close"
                    className="unstyled-button"
                    onClick={onClose}
                    data-impression-tracking="true"
                    aria-label="Close overlay"
                    type="button"
                  >
                    <CloseLineIcon />
                  </button>
                </>
              : <>
                  <button
                    data-qa-id="who-is-coming-close"
                    className="unstyled-button wic-overlay__close-overlay-btn"
                    onClick={onClose}
                    data-impression-tracking="true"
                    type="button"
                    aria-label="Close overlay"
                  >
                    <ArrowIcon />
                  </button>
                  <Typography variant="header2">Guestlist</Typography>
                </>}
          </div>
          {loading
            ? <div className="wic-overlay__loading">
                <span className="evt-spinner" />
                <Typography variant="paragraph2">Loading guestlist...</Typography>
              </div>
            : <>
                <WhoIsComingHeader
                  yes={yesRsvp}
                  maybe={includeMaybe ? maybeRsvp : undefined}
                  no={noRsvp}
                  no_reply={undecided}
                  query={query}
                  onQueryChange={setQuery}
                />
                <Guestlist
                  replies={replies}
                  query={query}
                  openCommentEditor={openCommentEditor}
                  setOpenCommentEditor={setOpenCommentEditor}
                  rsvpDetails={rsvpDetails}
                  removeAds={isFuneral || !showGifting}
                />
              </>}
        </div>
      </FocusTrap>
    </>
  );
};

WhoIsComingOverlay.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  rsvpDetails: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(
      PropTypes.shape({
        avatar_url: PropTypes.string,
        user_id: PropTypes.string,
        guest_id: PropTypes.string.isRequired,
        adults: PropTypes.number.isRequired,
        kids: PropTypes.number.isRequired,
        guest_name: PropTypes.string.isRequired,
        rsvp: PropTypes.string,
        guest_type: PropTypes.string.isRequired,
        rsvp_comment: PropTypes.string,
        rsvp_when: PropTypes.string,
        post_likes: PropTypes.number,
        liked_by: PropTypes.arrayOf(PropTypes.string),
        post_id: PropTypes.string.isRequired,
        date_viewed: PropTypes.string,
        delivery_status: PropTypes.string,
        replies: PropTypes.arrayOf(
          PropTypes.shape({
            user_id: PropTypes.string,
            guest_id: PropTypes.string.isRequired,
            guest_name: PropTypes.string.isRequired,
            comment: PropTypes.string.isRequired,
            comment_when: PropTypes.string.isRequired,
            post_likes: PropTypes.number,
            liked_by: PropTypes.arrayOf(PropTypes.string),
            post_id: PropTypes.string.isRequired,
          })
        ),
      })
    ),
  }),
};
